import { Card, type ContextMenuItem } from "@design";
import type { ReactNode } from "react";
import { Text } from "@/components/Text";
import { RowMenu } from "./RowMenu";

/**
 * One row in a Build entity list: a full-width interactive Card (the design
 * system's list-row shell) with an icon well, name + description, meta
 * (badges), and an overflow menu for secondary actions. The name is the
 * click target (a real link); the Card is only `interactive` because the row
 * contains its own controls — a nested <button> row would be invalid HTML.
 */
export function EntityRow({
  icon,
  name,
  description,
  meta,
  menuItems,
  onOpen,
}: Readonly<{
  icon: ReactNode;
  name: string;
  description?: string | null;
  meta?: ReactNode;
  menuItems?: ContextMenuItem[];
  onOpen: () => void;
}>) {
  return (
    <Card interactive className="w-full">
      <div className="flex items-center gap-3">
        {icon}
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 rounded text-left btn-focus-ring"
        >
          <Text variant="body" className="truncate">
            {name}
          </Text>
          {/* Always render the description line (empty when there's no
              description) so every row is the same height. `min-h` keeps the
              empty line from collapsing (an empty <p> has no line-box). */}
          <Text variant="body-sm" tone="secondary" className="min-h-[21px] truncate">
            {description ?? ""}
          </Text>{" "}
        </button>
        {meta && <div className="flex shrink-0 items-center gap-2">{meta}</div>}
        {menuItems && menuItems.length > 0 && <RowMenu name={name} items={menuItems} />}
      </div>
    </Card>
  );
}
